import type { RecordFormFieldConfig } from "./RecordFormWidget";
import { deriveRecordFormFields } from "./recordFormWidgetSeed";

/**
 * Widget-editor seeding for `record_form` widgets
 * (operator-task-fe-record-form-widget-seed-required-fields-from-create-schema).
 *
 * When the user picks a record type (and, for custom objects, an object
 * template) in a record_form widget whose Fields list is empty, the editor
 * fills that list with the create-screen fields: Name plus the object
 * template's text, text-area and dropdown attributes, required ones marked.
 * The user sees how the widget is configured and removes what they do not
 * want; the required fields are there, so the create action works.
 *
 * A Fields list that already has entries is never touched.
 */

type FetchObjectTemplateSchema = (objectTemplateId: string) => Promise<unknown>;

const isRecordForm = (widget: any) => widget?.type === "record_form";

const hasFields = (config: any) => Array.isArray(config?.fields) && config.fields.length > 0;

/** The record type + object template a widget's create form depends on. */
const seedKey = (config: any): string | null => {
    const recordType = typeof config?.record_type === "string" ? config.record_type.toUpperCase() : "";
    if (!recordType) {
        return null;
    }
    if (recordType === "CUSTOM_OBJECT") {
        return config?.object_template_id ? `${recordType}:${config.object_template_id}` : null;
    }
    return recordType;
};

/**
 * Returns the dashboard value with seeded Fields, or `null` when no widget
 * needs seeding. A widget is seeded when it is a record_form with an empty
 * Fields list whose record type / object template changed from `previous`.
 */
export const seedRecordFormWidgetFields = async (
    previous: any,
    next: any,
    fetchSchema: FetchObjectTemplateSchema
): Promise<any | null> => {
    const widgets: any[] | undefined = Array.isArray(next?.widgets) ? next.widgets : undefined;
    if (!widgets) {
        return null;
    }
    const previousWidgets: any[] = Array.isArray(previous?.widgets) ? previous.widgets : [];
    const targets = widgets
        .map((widget, index) => ({ widget, index }))
        .filter(({ widget, index }) => {
            if (!isRecordForm(widget) || hasFields(widget.config)) {
                return false;
            }
            const key = seedKey(widget.config);
            if (!key) {
                return false;
            }
            const before = previousWidgets[index];
            return !isRecordForm(before) || seedKey(before?.config) !== key;
        });
    if (targets.length === 0) {
        return null;
    }
    const seeded = [...widgets];
    for (const { widget, index } of targets) {
        const config = widget.config ?? {};
        let schema: unknown = null;
        if (seedKey(config)?.startsWith("CUSTOM_OBJECT:")) {
            try {
                schema = await fetchSchema(config.object_template_id);
            } catch {
                schema = null;
            }
            if (!schema) {
                // Without the template schema there is nothing useful to seed.
                continue;
            }
        }
        const fields: RecordFormFieldConfig[] = deriveRecordFormFields(schema, {
            includeName: !config.name_template,
        });
        if (fields.length > 0) {
            seeded[index] = { ...widget, config: { ...config, fields } };
        }
    }
    return seeded.some((widget, index) => widget !== widgets[index]) ? { ...next, widgets: seeded } : null;
};
